import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import sound, { SoundName } from 'lib/sound';

interface SoundContextValue {
  muted: boolean;
  musicEnabled: boolean;
  volume: number;
  toggleMute: () => void;
  toggleMusic: () => void;
  setVolume: (v: number) => void;
  play: (name: SoundName) => void;
  playTick: (secondsLeft: number) => void;
  resetTick: () => void;
  unlock: () => void;
}

const SoundContext = createContext<SoundContextValue | null>(null);

export function SoundProvider({ children }: { children: React.ReactNode }) {
  const [muted, setMuted] = useState(sound.isMuted());
  const [musicEnabled, setMusicEnabled] = useState(sound.isMusicEnabled());
  const [volume, setVolumeState] = useState(sound.getVolume());

  const toggleMute = useCallback(() => {
    sound.toggle();
    setMuted(sound.isMuted());
  }, []);

  const toggleMusic = useCallback(() => {
    sound.toggleMusic();
    setMusicEnabled(sound.isMusicEnabled());
  }, []);

  const setVolume = useCallback((v: number) => {
    sound.setVolume(v);
    setVolumeState(sound.getVolume());
    if (v > 0 && sound.isMuted()) {
      sound.setMuted(false);
      setMuted(false);
    }
  }, []);

  const play = useCallback((name: SoundName) => sound.play(name), []);
  const playTick = useCallback((secondsLeft: number) => sound.playTick(secondsLeft), []);
  const resetTick = useCallback(() => sound.resetTick(), []);
  const unlock = useCallback(() => sound.unlock(), []);

  const value = useMemo(
    () => ({
      muted,
      musicEnabled,
      volume,
      toggleMute,
      toggleMusic,
      setVolume,
      play,
      playTick,
      resetTick,
      unlock,
    }),
    [muted, musicEnabled, volume, toggleMute, toggleMusic, setVolume, play, playTick, resetTick, unlock]
  );

  return <SoundContext.Provider value={value}>{children}</SoundContext.Provider>;
}

export function useSound() {
  const ctx = useContext(SoundContext);
  if (!ctx) throw new Error('useSound must be used within SoundProvider');
  return ctx;
}
